import {type ReactNode, useCallback, useEffect, useRef, useState} from 'react';

import {
  AuthProvider,
  type AuthProviderProps,
  useAuth,
  UserManager,
  WebStorageStateStore,
} from 'oidc-react';

import {Loader} from './modules/common/components/Loader';
import {routes} from './modules/common/constants/routes';
import {config} from './modules/common/services/configService';
import {useAuthStore} from './modules/common/stores/authStore';
import {useUserStore} from './modules/common/stores/userStore';
import {toast} from './ui-lib/components';

const userManager = new UserManager({
  scope: 'openid profile email',
  authority: config.sso.loginUri,
  client_id: config.sso.clientId ?? '',
  redirect_uri: window.origin + routes.authRedirect,
  post_logout_redirect_uri: window.origin + routes.authRedirect,
  loadUserInfo: true,
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,
  revokeTokensOnSignout: true,
  userStore: new WebStorageStateStore({
    prefix: 'oidcAuthStore',
    store: localStorage,
  }),
});

const oidcConfig: AuthProviderProps = {autoSignIn: true, userManager};

export function AuthWrapper({children}: {children: ReactNode}): ReactNode {
  return (
    <AuthProvider {...oidcConfig}>
      <AuthWrapperHandler>{children}</AuthWrapperHandler>
    </AuthProvider>
  );
}

export function AuthWrapperHandler({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const setTokens = useAuthStore((state) => state.setTokens);
  const logout = useAuthStore((state) => state.logout);
  const setUserDetails = useUserStore((state) => state.setUserDetails);
  const {userData, signOutRedirect, isLoading: isAuthLoading} = useAuth();
  const [tokenReady, setTokenReady] = useState(false);
  const silentRenewPromiseRef = useRef<Promise<void> | null>(null);

  const handleLogout = useCallback(async () => {
    logout();
    await signOutRedirect();
  }, [logout, signOutRedirect]);

  const tryRenewOrLogout = useCallback(async (): Promise<void> => {
    if (silentRenewPromiseRef.current != null) {
      return silentRenewPromiseRef.current;
    }
    const renew = userManager
      .signinSilent()
      .then(() => undefined)
      .catch(() => {
        toast('Your session has expired. Please log in again.', 'error');
        void handleLogout();
      })
      .finally(() => {
        silentRenewPromiseRef.current = null;
      });
    silentRenewPromiseRef.current = renew;
    return renew;
  }, [handleLogout]);

  useEffect(() => {
    if (userData?.expired) {
      setTokenReady(false);
      void tryRenewOrLogout();
    } else if (
      userData?.access_token != null &&
      userData?.refresh_token != null
    ) {
      const timestamp = (userData.expires_at ?? 0) * 1000;
      const date = new Date(timestamp);
      setTokens(userData.access_token, userData.refresh_token, date);
      const userDetails: Record<string, unknown> = {
        name: undefined,
        email: undefined,
        email_verified: undefined,
        preferred_username: undefined,
        given_name: undefined,
        family_name: undefined,
      };
      Object.keys(userData.profile).forEach((key) => {
        if (key in userDetails) {
          userDetails[key] = userData.profile[key];
        }
      });
      setUserDetails(userDetails);
      setTokenReady(true);
    } else {
      setTokenReady(false);
    }
  }, [userData, setTokens, setUserDetails, tryRenewOrLogout]);

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') return;
      const {expiresAt, refreshToken} = useAuthStore.getState();
      if (refreshToken == null) return;
      const now = Date.now();
      const expiresAtMs = expiresAt instanceof Date ? expiresAt.getTime() : 0;
      if (expiresAtMs - now < 60_000) {
        void tryRenewOrLogout();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return (): void => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [tryRenewOrLogout]);

  useEffect(() => {
    userManager.events.addUserSignedOut(handleLogout);

    return (): void => {
      userManager.events.removeUserSignedOut(handleLogout);
    };
  }, [handleLogout]);

  if (!tokenReady || isAuthLoading || !userData) {
    return (
      <Loader
        text="Authenticating"
        stackProps={{flex: 1, justifyContent: 'center', alignItems: 'center'}}
      />
    );
  }

  return children;
}
